import React, { forwardRef, useCallback, useContext, useMemo } from 'react'
import { DraggableProvided } from 'react-beautiful-dnd'
import { UploadType } from 'services/api/uploads'
import styled from 'particles/themes/styled'
import { theme } from 'theme'
import { EDITOR_ROUTE_ENUM, ViewTypeContext } from 'components/Campaign'
import { useField, useFormikContext } from 'formik'
import { BLOCK_TYPE } from 'state/tasks'
import { BLOCK_TYPE as REGISTRATION_BLOCK_TYPE } from 'state/registrations'
import {
    TextBlockEditor as RegistrationTextBlock,
    PhotosBlockEditor as RegistrationPhotosBlock,
    PhotoSubmissionBlockEditor as RegistrationPhotoSubmissionBlock,
    SubmissionBlockEditor as RegistrationSubmissionBlockEditor,
    TextSubmissionBlockEditor as RegistrationTextSubmissionBlockEditor,
    QuestionBlockEditor as RegistrationQuestionBlockEditor,
} from './Registration'
import CloseButton from 'components/Shared/Button/CloseButton'
import Icon from 'components/Icon/Icon'
import Divider from 'components/Shared/Divider'
import InputField from 'components/Shared/Fields'
import Text from 'particles/Text'
import { useNewTranslation } from 'hooks'
import _ from 'lodash'
import Toggle from 'particles/Toggle'
import { fieldIdentifiers } from 'components/Shared/Comment'

export interface BlockFormEditorProps {
    blockUuid: string
    namePrefix: string
    isDisabled?: boolean
    isReadOnly?: boolean
    canEditCertainFields?: boolean
}

interface BlockEditorProps extends BlockFormEditorProps {
    type: BLOCK_TYPE | REGISTRATION_BLOCK_TYPE
    blockIndex: number
    onRemove: (blockIndex: number) => void
    isReadOnly?: boolean
    hideRemove?: boolean
    canEditCertainFields?: boolean
}

const DraggableBox = styled.div`
    cursor: grab;
`

const BlockEditorContainer = styled.div`
    padding: ${theme.newSpacing.l};
    display: flex;
    margin-bottom: ${theme.newSpacing.s};
    flex-direction: column;
    background: ${theme.newColors.neutral50};
    box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.06);
    border-radius: 4px;
    gap: ${theme.newSpacing.l};
    position: relative;

    & > .block-editor-title {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    & > ${DraggableBox} {
        position: absolute;
        top: 21px;
        left: 4px;
    }
`

const BlockEditor = forwardRef<any, BlockEditorProps & { draggableProvided?: DraggableProvided }>(
    function ForwardingBlockEditor(
        {
            blockIndex,
            namePrefix,
            isDisabled,
            type,
            onRemove,
            hideRemove,
            draggableProvided,
            isReadOnly = false,
            canEditCertainFields = false,
            blockUuid,
            ...props
        },
        ref,
    ) {
        const [t] = useNewTranslation()
        const viewType = useContext(ViewTypeContext)
        const blockEditorProps = { blockUuid, namePrefix, isDisabled }

        const handleRemove = useCallback(() => {
            onRemove(blockIndex)
        }, [blockIndex, onRemove])

        const { blockTitle, component, hideCloseButton } = useMemo(() => {
            let component = undefined,
                hideCloseButton = false,
                blockTitleKey = ''
            switch (type) {
                case BLOCK_TYPE.TEXT:
                    blockTitleKey = 'campaignEditor.customAndRegistrationField.textFieldHeader'
                    component = <TextBlockEditor {...blockEditorProps} isReadOnly={isReadOnly} />
                    break
                case BLOCK_TYPE.PHOTO_CAROUSEL:
                    blockTitleKey = 'campaignEditor.customAndRegistrationField.uploadPhotoFieldHeader'
                    component = <PhotosBlockEditor {...blockEditorProps} isReadOnly={isReadOnly} />
                    break
                case BLOCK_TYPE.PHOTO_SUBMISSION:
                    blockTitleKey = 'campaignEditor.customAndRegistrationField.imageUploadHeader'
                    component = <PhotoSubmissionBlockEditor {...blockEditorProps} isReadOnly={isReadOnly} />
                    break
                case BLOCK_TYPE.URL_SUBMISSION:
                    blockTitleKey = 'campaignEditor.customAndRegistrationField.urlFieldHeader'
                    component = <UrlSubmissionBlockEditor {...blockEditorProps} isReadOnly={isReadOnly} />
                    break
                case BLOCK_TYPE.TEXT_SUBMISSION:
                    blockTitleKey = 'campaignEditor.customAndRegistrationField.openEndedFieldHeader'
                    component = <TextSubmissionBlockEditor {...blockEditorProps} isReadOnly={isReadOnly} />
                    break
                case REGISTRATION_BLOCK_TYPE.TEXT:
                    blockTitleKey = 'campaignEditor.customAndRegistrationField.textFieldHeader'
                    component = <RegistrationTextBlock {...blockEditorProps} isReadOnly={isReadOnly} />
                    break
                case REGISTRATION_BLOCK_TYPE.PHOTOS:
                    blockTitleKey = 'campaignEditor.customAndRegistrationField.uploadPhotoFieldHeader'
                    component = <RegistrationPhotosBlock {...blockEditorProps} isReadOnly={isReadOnly} />
                    break
                case REGISTRATION_BLOCK_TYPE.QUESTION:
                    blockTitleKey = 'campaignEditor.customAndRegistrationField.multipleChoiceFieldHeader'
                    hideCloseButton = canEditCertainFields || isReadOnly
                    component = (
                        <RegistrationQuestionBlockEditor
                            {...blockEditorProps}
                            isReadOnly={hideCloseButton}
                            canEditCertainFields={canEditCertainFields}
                        />
                    )
                    break
                case REGISTRATION_BLOCK_TYPE.PHOTO_SUBMISSION:
                    blockTitleKey = 'campaignEditor.customAndRegistrationField.imageUploadHeader'
                    hideCloseButton = canEditCertainFields || isReadOnly
                    component = (
                        <RegistrationPhotoSubmissionBlock
                            {...blockEditorProps}
                            isReadOnly={hideCloseButton}
                            canEditCertainFields={canEditCertainFields}
                        />
                    )
                    break
                case REGISTRATION_BLOCK_TYPE.URL_SUBMISSION:
                    blockTitleKey = 'campaignEditor.customAndRegistrationField.urlFieldHeader'
                    hideCloseButton = canEditCertainFields || isReadOnly
                    component = (
                        <RegistrationSubmissionBlockEditor
                            {...blockEditorProps}
                            isReadOnly={hideCloseButton}
                            canEditCertainFields={canEditCertainFields}
                        />
                    )
                    break
                case REGISTRATION_BLOCK_TYPE.NUMBER_SUBMISSION:
                    blockTitleKey = 'campaignEditor.customAndRegistrationField.numberFieldHeader'
                    hideCloseButton = canEditCertainFields || isReadOnly
                    component = (
                        <RegistrationSubmissionBlockEditor
                            {...blockEditorProps}
                            isReadOnly={hideCloseButton}
                            canEditCertainFields={canEditCertainFields}
                        />
                    )
                    break
                case REGISTRATION_BLOCK_TYPE.TEXT_SUBMISSION:
                    blockTitleKey = 'campaignEditor.customAndRegistrationField.openEndedFieldHeader'
                    hideCloseButton = canEditCertainFields || isReadOnly
                    component = (
                        <RegistrationTextSubmissionBlockEditor
                            {...blockEditorProps}
                            isReadOnly={hideCloseButton}
                            canEditCertainFields={canEditCertainFields}
                        />
                    )
                    break
            }
            return {
                component,
                hideCloseButton,
                blockTitle: t(blockTitleKey),
            }
        }, [t, type, canEditCertainFields])

        return (
            <BlockEditorContainer ref={ref} {...props}>
                {!isDisabled && draggableProvided ? (
                    <DraggableBox
                        {...draggableProvided.dragHandleProps}
                        {...(draggableProvided.draggableProps, { style: undefined })}
                    >
                        {!isReadOnly && <Icon.Drag2Icon />}
                    </DraggableBox>
                ) : undefined}
                <div className="block-editor-title">
                    <Text scale="bodyM700" color="primary">
                        {blockTitle}
                    </Text>
                    {!isDisabled &&
                        !isReadOnly &&
                        !hideCloseButton &&
                        !hideRemove &&
                        viewType === EDITOR_ROUTE_ENUM.EDIT && (
                            <CloseButton
                                color={theme.newColors.neutral500}
                                size={20}
                                type="button"
                                onClick={handleRemove}
                            />
                        )}
                </div>
                {component}
            </BlockEditorContainer>
        )
    },
)

const TextBlockEditor: React.FC<BlockFormEditorProps> = ({ blockUuid, namePrefix, isReadOnly = false }) => {
    const [t] = useNewTranslation()
    return (
        <>
            <InputField.TextInputField
                label={t('campaignEditor.createTask.enterTitleHeader')}
                name={`${namePrefix}.title`}
                width="100%"
                isRequired
                isReadOnly={isReadOnly}
                identifier={`${blockUuid}_${fieldIdentifiers.blockTitle}`}
            />
            <InputField.TextAreaField
                label={t('campaignEditor.createTask.enterDescriptionHeader')}
                name={`${namePrefix}.description`}
                width="100%"
                isRequired
                isReadOnly={isReadOnly}
                identifier={`${blockUuid}_${fieldIdentifiers.blockDescription}`}
            />
        </>
    )
}

const PhotosBlockEditor: React.FC<BlockFormEditorProps> = ({ blockUuid, namePrefix, isReadOnly = false }) => {
    const [t] = useNewTranslation()
    const { setFieldValue } = useFormikContext()
    const [meta] = useField(`${namePrefix}.allow_download`)
    const { value: allowDownloadValue } = meta

    return (
        <>
            <TextBlockEditor blockUuid={blockUuid} namePrefix={namePrefix} isReadOnly={isReadOnly} />

            <InputField.ImageUploadField
                title={t('campaignEditor.customAndRegistrationField.uploadPhotoHeader')}
                presetType={UploadType.CAMPAIGN}
                maxFiles={10}
                name={`${namePrefix}.files`}
                uploadContainerWidth="100%"
                isReadOnly={isReadOnly}
                identifier={`${blockUuid}_${fieldIdentifiers.uploadPhotos}`}
            />
            <Divider />
            <Toggle
                isChecked={allowDownloadValue}
                isReadOnly={isReadOnly}
                handleChange={() => {
                    const updateValue = !Boolean(allowDownloadValue)
                    setFieldValue(`${namePrefix}.allow_download`, updateValue)
                }}
                label={t('campaignEditor.customAndRegistrationField.turnOnDownloadablePhotoHeader')}
                subtext={t('campaignEditor.customAndRegistrationField.turnOnDownloadablePhotoSubtext')}
                identifier={`${blockUuid}_${fieldIdentifiers.allowDownloadToggle}`}
            />
        </>
    )
}

const PhotoSubmissionBlockEditor: React.FC<BlockFormEditorProps> = ({ blockUuid, namePrefix, isReadOnly = false }) => {
    const [t] = useNewTranslation()
    return (
        <>
            <InputField.TextInputField
                label={t('campaignEditor.createTask.enterTitleHeader')}
                name={`${namePrefix}.title`}
                width="100%"
                isRequired
                isReadOnly={isReadOnly}
                identifier={`${blockUuid}_${fieldIdentifiers.blockTitle}`}
            />
            <InputField.NumberInputField
                title={t('campaignEditor.customAndRegistrationField.enterMaximumPhotosHeader')}
                subtitle={t('campaignEditor.customAndRegistrationField.enterMaximumPhotosSubtext')}
                name={`${namePrefix}.max_photos`}
                placeholder="0"
                isRequired
                isReadOnly={isReadOnly}
                identifier={`${blockUuid}_${fieldIdentifiers.numberOfPhotos}`}
            />
        </>
    )
}

const TextSubmissionBlockEditor: React.FC<BlockFormEditorProps> = ({ blockUuid, namePrefix, isReadOnly = false }) => {
    const [t] = useNewTranslation()
    return (
        <InputField.TextInputField
            name={`${namePrefix}.title`}
            width="100%"
            label={t('campaignEditor.createTask.enterTitleHeader')}
            isRequired
            isReadOnly={isReadOnly}
            identifier={`${blockUuid}_${fieldIdentifiers.blockTitle}`}
        />
    )
}

const UrlSubmissionBlockEditor = TextSubmissionBlockEditor

export default BlockEditor
